/**
 * React Flow node drag lifecycle: attach/detach nodes to/from sections on drag stop,
 * with preview highlighting during drag.
 */

import { useReactFlow, type Node, type OnNodeDrag } from "@xyflow/react";
import { useCallback, useRef } from "react";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasSectionPreview } from "../context/CanvasSectionPreviewContext";
import {
  buildDetachUpdates,
  buildParentUpdatesForAdoption,
  findIntersectingSections,
  getNodeAbsoluteBounds,
  getSectionAdoptionPreviewMemberIds,
  getSectionDragReparentState,
  getSectionDragStopParentUpdates,
  isSectionNode,
  pickTargetSection,
  shouldDetachFromParent,
} from "../utils/sectionMembership";

export function useCanvasNodeDragHandlers() {
  const { batchUpdateNodeParents } = useCanvas();
  const { getNodes, getInternalNode } = useReactFlow();
  const { setSectionPreview } = useCanvasSectionPreview();

  const latestDraggedRef = useRef<Node[]>([]);
  const previewRafRef = useRef<number | null>(null);

  const updateSectionDragPreview = useCallback(() => {
    const draggedNodes = latestDraggedRef.current;
    const nodes = getNodes();
    const nodesById = new Map(nodes.map((n) => [n.id, n]));
    const memberIds = new Set<string>();
    let targetSectionId: string | undefined;

    for (const dragged of draggedNodes) {
      if (
        dragged.type === "chatSession" ||
        dragged.type === "canvasConnector"
      ) {
        continue;
      }

      if (isSectionNode(dragged)) {
        const adoptIds = getSectionAdoptionPreviewMemberIds(
          dragged,
          nodes,
          getInternalNode,
        );
        if (adoptIds.size > 0) {
          targetSectionId = dragged.id;
          for (const adoptId of adoptIds) {
            memberIds.add(adoptId);
          }
        }

        const reparentPreview = getSectionDragReparentState(
          dragged,
          nodes,
          getInternalNode,
        );
        if (reparentPreview.previewTargetSectionId) {
          targetSectionId = reparentPreview.previewTargetSectionId;
        }
        for (const id of reparentPreview.previewMemberIds) {
          memberIds.add(id);
        }
        continue;
      }

      const bounds = getNodeAbsoluteBounds(dragged, getInternalNode, nodesById);

      const sections = findIntersectingSections(
        bounds,
        nodes,
        getInternalNode,
        dragged.id,
      );
      const target = pickTargetSection(sections, nodes);

      if (target && target.id !== dragged.parentId) {
        targetSectionId = target.id;
        memberIds.add(dragged.id);
      } else if (
        dragged.parentId &&
        shouldDetachFromParent(dragged, nodes, getInternalNode)
      ) {
        memberIds.add(dragged.id);
      }
    }

    if (memberIds.size > 0 || targetSectionId) {
      setSectionPreview({
        memberIds,
        targetSectionId,
        mode: "drag",
      });
    } else {
      setSectionPreview(null);
    }
  }, [getNodes, getInternalNode, setSectionPreview]);

  const onNodeDrag: OnNodeDrag = useCallback(
    (_event, _node, draggedNodes) => {
      latestDraggedRef.current = draggedNodes;
      if (previewRafRef.current !== null) return;
      previewRafRef.current = requestAnimationFrame(() => {
        previewRafRef.current = null;
        updateSectionDragPreview();
      });
    },
    [updateSectionDragPreview],
  );

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, _node, draggedNodes) => {
      if (previewRafRef.current !== null) {
        cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
      setSectionPreview(null);

      const nodes = getNodes();
      const nodesById = new Map(nodes.map((n) => [n.id, n]));
      const updates: Array<{
        nodeId: string;
        parentId: string | null;
        position: { x: number; y: number };
      }> = [];

      for (const dragged of draggedNodes) {
        if (
          dragged.type === "chatSession" ||
          dragged.type === "canvasConnector"
        ) {
          continue;
        }

        if (isSectionNode(dragged)) {
          updates.push(
            ...getSectionDragStopParentUpdates(dragged, nodes, getInternalNode),
          );
          continue;
        }

        const bounds = getNodeAbsoluteBounds(
          dragged,
          getInternalNode,
          nodesById,
        );

        const sections = findIntersectingSections(
          bounds,
          nodes,
          getInternalNode,
          dragged.id,
        );
        const target = pickTargetSection(sections, nodes);

        if (target && target.id !== dragged.parentId) {
          const adopt = buildParentUpdatesForAdoption(
            [dragged],
            target,
            nodes,
            getInternalNode,
          );
          updates.push(...adopt);
        } else if (shouldDetachFromParent(dragged, nodes, getInternalNode)) {
          const detach = buildDetachUpdates(
            [dragged.id],
            nodes,
            getInternalNode,
          );
          updates.push(...detach);
        }
      }

      if (updates.length > 0) {
        batchUpdateNodeParents(updates);
      }
    },
    [getNodes, getInternalNode, batchUpdateNodeParents, setSectionPreview],
  );

  return { onNodeDrag, onNodeDragStop };
}
